import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button, CircularProgress, Card, CardContent } from "@mui/material";
import axios from "axios";
import { useAuth } from "../AuthContext";

export default function NewsSummaryCard({ entity }) {
  const { user } = useAuth();
  const [summary, setSummary] = useState("");
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [error, setError] = useState(null);

  const fetchSummary = useCallback(async (force = false) => {
    if (!entity) return;
    setLoading(true);
    setError(null);
    try {
      const headers = {};
      if (user) {
        try {
          const token = await user.getIdToken();
          headers["Authorization"] = `Bearer ${token}`;
        } catch {
          /* ignore token fetch errors; treat as anon */
        }
      }
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/news-summary/${encodeURIComponent(entity)}`,
        { params: { force }, headers }
      );
      setSummary(res.data?.summary || "No summary available.");
      setLastUpdated(new Date());
    } catch (err) {
      console.error("Error fetching summary:", err);
      setError("Failed to fetch summary.");
      setSummary("");
    } finally {
      setLoading(false);
    }
  }, [entity, user]);

  // Auto-fetch when entity changes
  useEffect(() => {
    fetchSummary(false);
  }, [fetchSummary]);

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="h6">Company News Summary</Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button onClick={() => fetchSummary(true)} variant="outlined" size="small">
              Refresh
            </Button>
          </Box>
        </Box>

        <Typography variant="subtitle2" sx={{ mb: 2, color: "text.secondary" }}>
          {entity ? `Entity: ${entity}` : "No entity selected"}
          {lastUpdated && ` • Updated ${lastUpdated.toLocaleTimeString()}`}
        </Typography>

        {loading ? (
          <CircularProgress size={24} />
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <Typography sx={{ whiteSpace: "pre-wrap" }}>{summary}</Typography>
        )}
      </CardContent>
    </Card>
  );
}
